import { useState } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Layout } from "ui-components/Layout";
import { Success, Error_, LoadingDialog } from "ui-components/Alert";
import { useLocation } from "react-router-dom";
import { formRender, initialValues, custCarrierValidations } from "./cust-carrier-schema";
import { getCustomerCarrier, createCustomerCarrier } from "services/data.internal.service";
import InfoCustCarrier from "./components/InfoCustCarrier";
import { StripedDataGrid } from "ui-components/data-grids/StripedCheckboxDataGrid";
import { validateCustomerCarrier } from "layouts/views/common/validation";
import { BackButton } from "ui-components/Button";

const columns = [
  {
    field: "no",
    headerName: "No",
    sortable: false,
    width: 50,
  },
  {
    field: "shippingZone",
    headerName: "Shipping Zone",
    sortable: false,
    width: 300,
    flex: 1,
  },
  {
    field: "carrierCode",
    headerName: "Carrier Code",
    sortable: false,
    width: 300,
    flex: 1,
  },
  {
    field: "b2BCarrierCode",
    headerName: "B2B Carrier Code",
    sortable: false,
    width: 300,
    flex: 1,
  },
];

function AddCustCarrier2() {
  const location = useLocation();
  const { customerCode } = location.state;
  const [alert, setAlert] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const createCustCarrierMutation = useMutation(createCustomerCarrier, {
    onSuccess: (data) => setAlert(<Success data={data} onChange={setAlert} />),
    onError,
  });
  const [details, setDetails] = useState([]);
  const { isLoading } = useQuery({
    queryKey: ["customer-carrier", customerCode],
    queryFn: () => getCustomerCarrier({ customerCode }),
    enabled: Boolean(customerCode),
    onSuccess: (data) =>
      setDetails(data.data.data.map((x, i) => ({ ...x, no: i + 1, newItem: false }))),
    onError,
  });

  const form = useForm({
    defaultValues: { ...initialValues, customerCode },
    values: formValues || { ...initialValues, customerCode },
    resolver: yupResolver(custCarrierValidations),
  });
  
  const {
    getValues,
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    reset,
  } = form;
  
  const infoProps = { getValues, control, errors, setValue, formRender };
  
  const onAdd = (data) => {
    const isDuplicate = validateCustomerCarrier(details, data);
    if (isDuplicate) {
      setAlert(<Error_ content="Duplicate Shipping Zone" onChange={setAlert} />);
      return;
    }
    if (formValues === null) {
      setDetails([...details, { ...data, no: details.length + 1, newItem: true }]);
    } else {
      setDetails(
        details.map((x) => {
          if (x.no === data.no) return data;
          return x;
        })
      );
      setFormValues(null);
    }

    reset();
  };

  const handleUpdateAll = () => {
    const values = getValues();
    const { carrierCode, b2BCarrierCode } = values;

    if (!carrierCode && !b2BCarrierCode) {
      setAlert(<Error_ content="No Carrier Code / B2B Carrier Code Selected." onChange={setAlert} />);
      return;
    }

    const updated = details.map((item) => ({
      ...item,
      carrierCode: carrierCode || item.carrierCode,
      b2BCarrierCode: b2BCarrierCode || item.b2BCarrierCode,
    }));

    setDetails(updated);
    reset();
  };

  const onSubmit2 = () => {
    //window.scrollTo(0, 0);
    createCustCarrierMutation.mutate({ customerCode, req: { data: details } }, {});
  };

  return (
    <Layout>
      {isLoading && <LoadingDialog />}
      {alert}
      <form onSubmit={handleSubmit(onAdd)} noValidate>
        <InfoCustCarrier {...infoProps} />
        <MDBox width="100%" display="flex" justifyContent="center" margin="1rem 0">
          {formValues === null ? (
            <>
              <MDButton type="submit" variant="gradient" color="info">
                Add
              </MDButton>
              <MDButton
                variant="gradient"
                color="success"
                sx={{ marginLeft: "1.5rem" }}
                onClick={handleUpdateAll}
              >
                Update All
              </MDButton>
            </>
          ) : (
            <>
              <MDButton type="submit" variant="gradient" color="success" disabled={!isDirty}>
                Update
              </MDButton>
              <MDButton
                sx={{ marginLeft: "1.5rem" }}
                variant="outlined"
                color="info"
                onClick={() => setFormValues(null)}
              >
                Back
              </MDButton>
            </>
          )}
        </MDBox>
        <StripedDataGrid
          onRowClick={(v) => setFormValues(v.row)}
          columns={columns}
          rows={details.map((detail, i) => ({ id: i + 1, ...detail }))}
        />
      </form>
      <MDBox mt={2} width="100%" display="flex" justifyContent="center">
        <MDButton type="submit" variant="gradient" color="info" onClick={onSubmit2}>
          Submit
        </MDButton>
        <BackButton url="/setup/customer" />
      </MDBox>
    </Layout>
  );
}

export default AddCustCarrier2;